import { FC } from "react";
import { FieldInputProps } from "react-final-form";

import { IPickerListItem, Picker } from "@livechat/design-system-react-components";

import { PriorityPickerListItem } from "components/picker-items/priorityPickerItems";
import PriorityIndicator from "components/priority/PriorityIndicator";
import { TICKET_PRIORITY_LABEL_MAP } from "constants/tickets/priorities";
import { TicketPriority } from "interfaces/entities/ticket";
import { shortPickerStyles } from "routes/automations/shared/styles";
import { IRuleTriggerPriority } from "store/entities/rules/interfaces";

import { changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerPriority>;
}

export const PriorityTrigger: FC<IProps> = ({ input }) => {
  const equalityOptions = [createEqualityOption("is"), createEqualityOption("isNot")];

  const selectedEqualityOptions = [createEqualityOption(input.value.invert ? "isNot" : "is")];

  const handleSelectEqualityOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    const selectedOption = selectedOptions[0];
    const newInvert = selectedOption.key === "isNot";
    changeTriggerValues(input, { invert: newInvert });
  };

  const priorityOptions = [TicketPriority.Low, TicketPriority.Medium, TicketPriority.High, TicketPriority.Urgent].map(
    createPriorityOption
  );

  const selectedPriorityOptions = input.value.in.map(createPriorityOption);

  const handleSelectPriority = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions) {
      changeTriggerValues(input, { in: [] });
      return;
    }

    const newPriorities = selectedOptions.map(opt => parseInt(opt.key, 10));
    changeTriggerValues(input, { in: newPriorities });
  };

  return (
    <>
      <Picker
        options={equalityOptions}
        selected={selectedEqualityOptions}
        onSelect={handleSelectEqualityOption}
        hideClearButton
        searchDisabled
        css={shortPickerStyles}
      />
      <Picker
        type="multi"
        options={priorityOptions}
        selected={selectedPriorityOptions}
        onSelect={handleSelectPriority}
        searchDisabled
        placeholder="Select one priority or more…"
      />
    </>
  );
};

const createEqualityOption = (key: "isNot" | "is"): IPickerListItem => ({
  key,
  name: key === "isNot" ? "is not" : "is",
});

const createPriorityOption = (priority: TicketPriority): IPickerListItem => ({
  key: priority.toString(),
  name: TICKET_PRIORITY_LABEL_MAP[priority],
  customElement: {
    listItemBody: <PriorityPickerListItem priority={priority} />,
  },
  selectedTagOptions: {
    leftNode: <PriorityIndicator priority={priority} />,
  },
});
